import {useState, type FormEvent} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Hint} from '@/components/ui/hint'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {PorQue} from '@/components/ui/por-que'
import {FieldError} from '@/components/field-error'
import {AuthSeal} from '@/components/auth-seal'
import {AuthShell} from '@/components/auth-shell'
import {useSupportRequest} from '@/hooks/empresa/identidad/use-email-verification'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {CUALQUIER_DOCUMENTO, DOCUMENTO_MAX} from '@/lib/documento'
import {validateCedula, validateRequired} from '@/lib/validation'
import type {components} from '@/lib/api-schema'

type ResetRequestResponse = components['schemas']['ResetRequestResponse']

/** El enlace solo viaja por correo: la respuesta nunca revela si la cuenta existe ni trae el token (F5). */
export function ResetRequestPage() {
  const {t} = useTranslation()
  const [cedula, setCedula] = useState('')
  const [cedulaError, setCedulaError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ResetRequestResponse | null>(null)
  // Recuperación sin correo: el caso va a Kairos para un restablecimiento manual.
  const support = useSupportRequest()
  const [supportOpen, setSupportOpen] = useState(false)
  const [supportDetail, setSupportDetail] = useState('')
  const [supportDetailError, setSupportDetailError] = useState<string | undefined>(undefined)
  const [supportSent, setSupportSent] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const problem = validateCedula(cedula, CUALQUIER_DOCUMENTO)
    setCedulaError(problem ?? undefined)
    if (problem) return
    setError(null)
    setSubmitting(true)
    try {
      const {data} = await api.post<ResetRequestResponse>('/auth/reset-request', {cedula})
      // Deliberadamente NO se lee `reset_token`: la única entrega es el correo.
      setResult(data)
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSupport(e: FormEvent) {
    e.preventDefault()
    // Sin validar acá, una cédula con typo recibe 400 y la pantalla igual diría «recibimos su solicitud».
    const cedulaProblem = validateCedula(cedula)
    const problem = validateRequired(supportDetail)
    setCedulaError(cedulaProblem ?? undefined)
    setSupportDetailError(problem ?? undefined)
    if (cedulaProblem || problem) return
    try {
      await support.mutateAsync({cedula, detail: supportDetail})
    } finally {
      setSupportSent(true)
    }
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl tracking-tight text-foreground">{t('setPassword.requestTitle')}</h1>

      {result ? (
        <div className="mt-8 space-y-4" data-testid="reset-requested">
          <AuthSeal />
          <p className="text-sm text-foreground">{t('setPassword.requestSent')}</p>
          <PorQue
            label={t('setPassword.requestNoEmailLabel')}
            testId="reset-no-email-trigger"
            contenidoTestId="reset-no-email-hint">
            {t('setPassword.requestNoEmailHint')}
          </PorQue>
          <Link to="/login" className="block text-center text-sm text-primary hover:underline">
            {t('setPassword.backToLogin')}
          </Link>
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5" data-testid="reset-request-form">
            <div className="space-y-2">
              <Label htmlFor="reset-cedula">{t('documento.label')}</Label>
              <Input
                id="reset-cedula"
                value={cedula}
                onChange={(e) => {
                  setCedula(e.target.value)
                  setCedulaError(undefined)
                }}
                onBlur={() => setCedulaError(validateCedula(cedula, CUALQUIER_DOCUMENTO) ?? undefined)}
                aria-invalid={!!cedulaError}
                required
                maxLength={DOCUMENTO_MAX.PA}
              />
              <FieldError error={cedulaError} />
              {/* Sin selector de tipo: acá se busca un documento, único en la plataforma, no se declara uno. */}
              <Hint>{t('documento.hintPA')}</Hint>
            </div>
            {error && <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {t('setPassword.requestSubmit')}
            </Button>
            <Hint className="text-center">{t('setPassword.requestVerifiedOnly')}</Hint>
          </form>

          <div className="surface-divider mt-7 rounded-lg border border-dashed px-4 py-4" data-testid="support-request">
            {supportSent ? (
              <p className="text-sm text-muted-foreground" data-testid="support-sent">
                {t('setPassword.supportSent')}
              </p>
            ) : supportOpen ? (
              <form onSubmit={handleSupport} noValidate className="space-y-3" data-testid="support-request-form">
                <p className="text-sm font-medium text-foreground">{t('setPassword.supportTitle')}</p>
                <Hint>{t('setPassword.supportBody')}</Hint>
                <div className="space-y-2">
                  <Label htmlFor="support-detail">{t('setPassword.supportDetailLabel')}</Label>
                  <textarea
                    id="support-detail"
                    value={supportDetail}
                    onChange={(e) => {
                      setSupportDetail(e.target.value)
                      setSupportDetailError(undefined)
                    }}
                    onBlur={() => setSupportDetailError(validateRequired(supportDetail) ?? undefined)}
                    aria-invalid={!!supportDetailError}
                    maxLength={1000}
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  />
                  <FieldError error={supportDetailError} />
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full"
                  disabled={support.isPending || cedula.trim().length < 6}>
                  {t('setPassword.supportSubmit')}
                </Button>
              </form>
            ) : (
              <button
                type="button"
                data-testid="support-request-open"
                onClick={() => setSupportOpen(true)}
                className="text-sm text-primary hover:underline">
                {t('auth.noEmailRecover')} {t('auth.requestSupport')}
              </button>
            )}
          </div>

          <Link to="/login" className="mt-6 block text-center text-sm text-primary hover:underline">
            {t('setPassword.backToLogin')}
          </Link>
        </>
      )}
    </AuthShell>
  )
}
